import { useInvoiceQueries } from "@lib/apiQueries";
import { AppTag } from "@lib/components/AppComponents";
import AppRowContainer from "@lib/components/AppComponents/AppRowContainer";
import MiniList from "@lib/components/MiniComponents/Dashboard/MiniList";
import MonthlyInvoiceChart from "@lib/components/MiniComponents/Dashboard/MonthlyInvoiceChart";
import CustomPieChart from "@lib/components/MiniComponents/Dashboard/PieChart";
import Revenue from "@lib/components/MiniComponents/Dashboard/Revenue";
import {
  CoinPriceTextFormat,
  CustomerLogo,
  CustomerName,
  DateRangeFilter,
} from "@lib/components/MiniComponents/Others";
import { StyledFlex } from "@lib/components/MiniComponents/Table/index.styled";
import { InvoiceDrawer } from "@lib/components/PageComponents/Invoice";
import {
  invoiceDueDateColor,
  invoiceStatusColor,
} from "@lib/helpers/ColorHelper";
import useScreen from "@lib/hooks/useScreen";
import { ApiParams, CurrencyEnum, Invoice } from "@lib/types";
import { Col } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

type Props = {
  dateBetween: [string, string];
  setDateBetween: (date: [string, string]) => void;
};

export default function ManagementInvoices({ dateBetween }: Props) {
  const { messages } = useIntl();
  const { isMobile } = useScreen();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [selected, setSelected] = useState<Invoice>();

  useEffect(() => {
    if (!openDrawer) setSelected(null);
  }, [openDrawer]);

  const toggleDrawer = (status?: boolean) => {
    if (typeof status !== "undefined") setOpenDrawer(status);
    else setOpenDrawer(!openDrawer);
  };

  const handleSelectInvoice = (invoice: Invoice) => {
    setSelected(invoice);
    toggleDrawer(true);
  };

  const params = useMemo(() => {
    return {
      s: [
        {
          $and: [
            { createdAt: { $gte: dateBetween[0] } },
            { createdAt: { $lte: dateBetween[1] } },
          ],
        },
      ],
      join: ["customer"],
    } as ApiParams;
  }, [dateBetween]);

  const { invoicesQuery } = useInvoiceQueries({
    messages,
    queryKey: ["dashboard-invoices", JSON.stringify(params)],
    params,
    enabled: !!params,
  });
  const apiData = invoicesQuery.data?.data?.data;
  const invoiceCurrency = CurrencyEnum.USD;
  const invoiceTotalAmount = useMemo(() => {
    let amount = 0;
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        amount += Number(apiData[index].amount);
      }
    }
    return amount;
  }, [apiData]);
  const invoiceTotalPaid = useMemo(() => {
    let amount = 0;
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        amount += Number(apiData[index].paid);
      }
    }
    return amount;
  }, [apiData]);

  const invoiceStatusGroup = useMemo(() => {
    const data: {
      name: string;
      value: number;
      color: string;
    }[] = [];
    if (apiData) {
      for (let index = 0; index < apiData.length; index++) {
        const item = apiData[index];
        const name =
          (messages["enums.status"][item.status] as string) || item.status;
        const findIndex = data.findIndex((d) => d.name == name);
        if (findIndex == -1) {
          data.push({
            name: name,
            value: 1,
            color: invoiceStatusColor(item.status),
          });
        } else {
          data[findIndex].value++;
        }
      }
    }
    return data.sort((a, b) => b.value - a.value);
  }, [apiData, messages]);

  return (
    <AppRowContainer>
      <Col xs={24} md={12} lg={12}>
        <Revenue
          title={messages["common.revenue"] as string}
          data={{
            currency: invoiceCurrency,
            paid: invoiceTotalPaid,
            total: invoiceTotalAmount,
          }}
          extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
          refetch={invoicesQuery.refetch}
          loading={invoicesQuery.isLoading || invoicesQuery.isFetching}
          cardProps={{
            borderFull: true,
          }}
        />
      </Col>

      <Col xs={24} md={12} lg={12}>
        <CustomPieChart
          title={messages["common.invoiceStatus"] as string}
          graph="graph2"
          data={invoiceStatusGroup}
          extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
          refetch={invoicesQuery.refetch}
          loading={invoicesQuery.isLoading || invoicesQuery.isFetching}
          cardProps={{
            borderFull: true,
          }}
        />
      </Col>

      <Col xs={24} md={12} lg={12}>
        <MiniList
          title={messages["common.upcomingInvoices"] as string}
          cardProps={{
            borderFull: true,
            heightFull: true,
          }}
          style={isMobile ? { height: "auto", maxHeight: "50vh" } : undefined}
          heightFull={isMobile}
          extra={<DateRangeFilter value={dateBetween} onlyShowDate />}
          refetch={invoicesQuery.refetch}
          loading={invoicesQuery.isLoading || invoicesQuery.isFetching}
          data={(apiData || [])
            // sort by due date
            ?.sort((a, b) => dayjs(b.dueDate).diff(dayjs(a.dueDate), "day"))
            .map((item) => ({
              onClick: () => {
                handleSelectInvoice(item);
              },
              avatar: <CustomerLogo customer={item?.customer} />,
              title: (
                <StyledFlex style={{ gap: 4, justifyContent: "space-between" }}>
                  <CustomerName customer={item?.customer} noLogo />

                  <CoinPriceTextFormat
                    amount={item.amount}
                    showCurrency={item?.currency}
                    align="left"
                  />
                </StyledFlex>
              ),
              description: (
                <StyledFlex style={{ gap: 4, justifyContent: "space-between" }}>
                  <AppTag color={invoiceStatusColor(item.status)}>
                    {(messages["enums.status"][item.status] as string) ||
                      item.status}
                  </AppTag>
                  <AppTag color={invoiceDueDateColor(item.dueDate)}>
                    {dayjs(item.dueDate).fromNow()}
                  </AppTag>
                </StyledFlex>
              ),
            }))}
        />

        <InvoiceDrawer
          toggleDrawer={setOpenDrawer}
          openDrawer={openDrawer}
          invoice={selected}
          setSelectedInvoice={handleSelectInvoice}
          invoiceListQueryKey={["invoices"]}
        />
      </Col>

      <Col xs={24} md={12} lg={12}>
        <MonthlyInvoiceChart />
      </Col>
    </AppRowContainer>
  );
}
